/* ────────────────────────────────────────────────────────────────────────
   Leave Policy — types, options and shared helpers (single source of truth).

   A leave policy defines, per leave type, the annual entitlement, whether it
   accrues monthly, and how many days may carry forward. Matches the
   POST /api/leave-policies contract.
   ──────────────────────────────────────────────────────────────────────── */

export interface LeavePolicy {
  id?: number;
  leaveType: string;
  daysPerYear: number;
  isAccrual: boolean;
  maxCarryForward: number;
}

// Payload sent to create/update (no id).
export type LeavePolicyPayload = Omit<LeavePolicy, "id">;

export interface LeaveTypeOption {
  value: string;
  label: string;
}

// Fallback leave types (mirrors the Add Leave form). The form also tries to load
// the tenant's configured leave types from the API and falls back to these.
export const LEAVE_TYPE_OPTIONS: LeaveTypeOption[] = [
  { value: "Sick leave", label: "Sick leave" },
  { value: "Urgent leave", label: "Urgent leave" },
  { value: "Maternity and paternity leave", label: "Maternity and paternity leave" },
  { value: "Public and religious holidays", label: "Public and religious holidays" },
  { value: "Bereavement leave", label: "Bereavement leave" },
  { value: "Time off in lieu (TOIL)", label: "Time off in lieu (TOIL)" },
  { value: "Unpaid leave", label: "Unpaid leave" },
  { value: "Paid leave", label: "Paid leave" },
  { value: "Other leave", label: "Other leave" },
];

// The 12-month accrual cadence used across the UI.
export const MONTHS_PER_YEAR = 12;

/**
 * Monthly accrual = annual entitlement / 12. Returned both raw (for further
 * calc) and pre-formatted to 2 decimals for display.
 */
export const getMonthlyAccrual = (daysPerYear: number | string | null | undefined) => {
  const annual = Number(daysPerYear);
  const perMonth = Number.isFinite(annual) && annual > 0 ? annual / MONTHS_PER_YEAR : 0;
  return { perMonth, perMonthLabel: perMonth.toFixed(2) };
};
